import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Chip, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  Switch, CircularProgress, Snackbar, Alert, InputAdornment,
  Checkbox, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Avatar, Tooltip,
  CardContent, MenuItem, Select, FormControl, InputLabel, alpha,
} from '@mui/material';
import {
  Add, Edit, Search, Refresh, Payment,
  ExpandMore, ExpandLess,
  UploadFile, ImageOutlined, Group, PersonAdd, Lock, LockOpen, Shield, CheckCircle,
  Cancel, Security, Settings,
} from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { formatDateTime } from '../utils/dateUtils';

// ALL_ROLES is now loaded dynamically from the API; this is the fallback
const FALLBACK_ROLES = [
  { key: 'superadmin', label: 'Super Admin', color: '#b91c1c' },
  { key: 'admin',      label: 'Admin',       color: '#1a237e' },
  { key: 'manager',    label: 'Manager',     color: '#0369a1' },
  { key: 'support',    label: 'Support',     color: '#0891b2' },
  { key: 'vendor',     label: 'Vendor',      color: '#d97706' },
];


const emptyForm    = { name: '', phone: '', email: '', password: '', roles: [] };
const EMPTY_PLAN   = { frequency: 'monthly', amount: '', advance_paid: '', start_date: new Date().toISOString().slice(0,10), notes: '' };
const EMPTY_COL    = { amount: '', paid_date: new Date().toISOString().slice(0,10), due_date: '', method: 'bank', status: 'paid', notes: '' };

const STATUS_COLOR = { paid: 'success', pending: 'warning', partial: 'info', overdue: 'error' };
const METHOD_LABEL = { cash: '💵 Cash', upi: '📱 UPI', bank: '🏦 Bank', neft: '🏦 NEFT', rtgs: '🏦 RTGS', other: '📋 Other' };
const FREQ_LABEL   = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Vendor Payments Dialog ────────────────────────────────────────────────────
function VendorPaymentsDialog({ vendor, open, onClose }) {
  const [plans, setPlans]           = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [collections, setCollections]   = useState({});
  const [expandedPlan, setExpandedPlan] = useState(null);

  const [planDialog, setPlanDialog]   = useState(false);
  const [editPlan, setEditPlan]       = useState(null);
  const [planForm, setPlanForm]       = useState(EMPTY_PLAN);
  const [savingPlan, setSavingPlan]   = useState(false);

  const [colDialog, setColDialog]     = useState(false);
  const [colPlanId, setColPlanId]     = useState(null);
  const [editCol, setEditCol]         = useState(null);
  const [colForm, setColForm]         = useState(EMPTY_COL);
  const [savingCol, setSavingCol]     = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [viewImage, setViewImage]     = useState(null); // full-screen image viewer

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const [summary, setSummary] = useState(null);

  const loadPlans = useCallback(async () => {
    if (!vendor) return;
    setPlansLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getVendorPaymentPlans, { vendor_id: vendor.id });
    setPlans(res?.plans ?? []);
    setPlansLoading(false);
  }, [vendor]);

  const loadSummary = useCallback(async () => {
    if (!vendor) return;
    const res = await InvokeApi(Config.base_url + apiList.getVendorPaymentSummary, { vendor_id: vendor.id });
    if (res?.code === '200') setSummary(res);
  }, [vendor]);

  useEffect(() => {
    if (open && vendor) { loadPlans(); loadSummary(); }
  }, [open, vendor, loadPlans, loadSummary]);

  const loadCollections = async (planId) => {
    const res = await InvokeApi(Config.base_url + apiList.getVendorPaymentCollections, { plan_id: planId });
    setCollections(prev => ({ ...prev, [planId]: res?.collections ?? [] }));
  };

  const togglePlan = async (planId) => {
    if (expandedPlan === planId) { setExpandedPlan(null); return; }
    setExpandedPlan(planId);
    if (!collections[planId]) await loadCollections(planId);
  };

  const openAddPlan  = () => { setEditPlan(null); setPlanForm(EMPTY_PLAN); setPlanDialog(true); };
  const openEditPlan = (p) => {
    setEditPlan(p);
    setPlanForm({ frequency: p.frequency, amount: p.amount, advance_paid: p.advance_paid, start_date: p.start_date, notes: p.notes || '' });
    setPlanDialog(true);
  };

  const handleSavePlan = async () => {
    if (!planForm.amount || parseFloat(planForm.amount) <= 0) {
      setSnack({ open: true, msg: 'Amount is required', sev: 'warning' }); return;
    }
    setSavingPlan(true);
    const endpoint = editPlan ? apiList.editVendorPaymentPlan : apiList.addVendorPaymentPlan;
    const payload  = editPlan
      ? { id: editPlan.id, ...planForm }
      : { vendor_id: vendor.id, ...planForm };
    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSavingPlan(false);
    if (res?.code === '200') {
      setSnack({ open: true, msg: editPlan ? 'Plan updated' : 'Plan created', sev: 'success' });
      setPlanDialog(false); loadPlans(); loadSummary();
    } else { setSnack({ open: true, msg: res?.message || 'Failed', sev: 'error' }); }
  };

  const openAddCol  = (planId) => {
    setColPlanId(planId); setEditCol(null); setColForm(EMPTY_COL);
    setReceiptFile(null); setReceiptPreview(null);
    setColDialog(true);
  };
  const openEditCol = (planId, col) => {
    setColPlanId(planId); setEditCol(col);
    setColForm({ amount: col.amount, paid_date: col.paid_date, due_date: col.due_date || '', method: col.method, status: col.status, notes: col.notes || '' });
    setReceiptFile(null);
    setReceiptPreview(col.receipt_image || null);
    setColDialog(true);
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const handleSaveCol = async () => {
    if (!colForm.amount || parseFloat(colForm.amount) <= 0) {
      setSnack({ open: true, msg: 'Amount is required', sev: 'warning' }); return;
    }
    setSavingCol(true);

    // Always use FormData so we can attach the receipt file
    const fd = new FormData();
    if (editCol) {
      fd.append('id', editCol.id);
    } else {
      fd.append('plan_id',   colPlanId);
      fd.append('vendor_id', vendor.id);
    }
    fd.append('amount',   colForm.amount);
    fd.append('paid_date', colForm.paid_date);
    if (colForm.due_date) fd.append('due_date', colForm.due_date);
    fd.append('method',   colForm.method);
    fd.append('status',   colForm.status);
    if (colForm.notes) fd.append('notes', colForm.notes);
    if (receiptFile)   fd.append('receipt_image', receiptFile);

    const endpoint = editCol ? apiList.editVendorPaymentCollection : apiList.addVendorPaymentCollection;
    const res = await postImg(Config.base_url + endpoint, fd);
    setSavingCol(false);
    if (res?.code === '200') {
      setSnack({ open: true, msg: editCol ? 'Payment updated' : 'Payment recorded', sev: 'success' });
      setColDialog(false);
      loadCollections(colPlanId); loadPlans(); loadSummary();
    } else { setSnack({ open: true, msg: res?.message || 'Failed', sev: 'error' }); }
  };

  if (!vendor) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Payment sx={{ color: '#ff6f00' }} />
          <Box>
            <Typography fontWeight={700} color="#1a237e">Vendor Payments — {vendor.name}</Typography>
            <Typography fontSize={12} color="text.secondary">Track payments made to this vendor</Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, px: 3 }}>
          {/* ── Summary strip ── */}
          {summary && (
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                { label: 'Total Paid',    val: summary.total_paid,    color: '#2e7d32', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: 'Pending',       val: summary.total_pending, color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
                { label: 'Overdue',       val: summary.total_overdue, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
              ].map(({ label, val, color, bg, border }) => (
                <Grid item xs={4} key={label}>
                  <Card sx={{ border: `1px solid ${border}`, bgcolor: bg, borderRadius: 1 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography fontSize={10} color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                      <Typography fontWeight={800} fontSize={16} sx={{ color }}>
                        ₹{parseFloat(val || 0).toLocaleString('en-IN')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* ── Plans list ── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography fontWeight={700} color="#1a237e">Payment Plans</Typography>
            <Button size="small" variant="contained" startIcon={<Add />} onClick={openAddPlan}
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontSize: 11 }}>
              Add Plan
            </Button>
          </Box>

          {plansLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: '#1a237e' }} /></Box>
          ) : plans.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, background: '#f8fafc', borderRadius: 2 }}>
              <Typography color="text.secondary">No payment plans yet</Typography>
              <Typography variant="caption" color="text.secondary">Add a plan to start tracking payments</Typography>
            </Box>
          ) : (
            plans.map((plan) => {
              const isExpanded = expandedPlan === plan.id;
              const cols = collections[plan.id] || [];
              return (
                <Card key={plan.id} sx={{ mb: 2, border: '1px solid #e8eaf6', borderRadius: 2, overflow: 'hidden' }}>
                  {/* Plan header */}
                  <Box sx={{ p: 2, background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography fontWeight={700} fontSize={14} color="#1a237e">
                          ₹{parseFloat(plan.amount).toLocaleString('en-IN')} / {FREQ_LABEL[plan.frequency] || plan.frequency}
                        </Typography>
                        <Chip label={`${plan.total_collections} payments`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#e8eaf6', color: '#1a237e' }} />
                        {plan.overdue_count > 0 && <Chip label={`${plan.overdue_count} overdue`} size="small" color="error" sx={{ fontSize: 10, height: 20 }} />}
                      </Box>
                      <Typography fontSize={11} color="text.secondary">
                        Start: {fmtDate(plan.start_date)} · Total paid: ₹{parseFloat(plan.total_paid || 0).toLocaleString('en-IN')}
                        {plan.advance_paid > 0 && ` · Advance: ₹${parseFloat(plan.advance_paid).toLocaleString('en-IN')}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openAddCol(plan.id)}
                        sx={{ fontSize: 11, borderColor: '#1a237e', color: '#1a237e' }}>
                        Record Payment
                      </Button>
                      <IconButton size="small" onClick={() => openEditPlan(plan)} sx={{ color: '#1565c0' }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => togglePlan(plan.id)} sx={{ color: '#64748b' }}>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Collections table */}
                  {isExpanded && (
                    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                      {cols.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                          No payments recorded yet
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: '#f8fafc' }}>
                                {['Date', 'Amount', 'Method', 'Status', 'Receipt', 'Notes', 'By', ''].map(h => (
                                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#475569', py: 1 }}>{h}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cols.map((col) => (
                                <TableRow key={col.id} hover>
                                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(col.paid_date)}</TableCell>
                                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>₹{parseFloat(col.amount).toLocaleString('en-IN')}</TableCell>
                                  <TableCell sx={{ fontSize: 11 }}>{METHOD_LABEL[col.method] || col.method}</TableCell>
                                  <TableCell>
                                    <Chip label={col.status} size="small" color={STATUS_COLOR[col.status] || 'default'} sx={{ fontWeight: 600, fontSize: 10, height: 20 }} />
                                  </TableCell>
                                  <TableCell>
                                    {col.receipt_image ? (
                                      <Box
                                        component="img"
                                        src={col.receipt_image}
                                        alt="receipt"
                                        onClick={() => setViewImage(col.receipt_image)}
                                        sx={{
                                          width: 40, height: 40, objectFit: 'cover',
                                          borderRadius: 1, border: '1px solid #e2e8f0',
                                          cursor: 'zoom-in',
                                          '&:hover': { opacity: 0.8 },
                                        }}
                                      />
                                    ) : (
                                      <ImageOutlined sx={{ fontSize: 20, color: '#cbd5e1' }} />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11, maxWidth: 120 }}>
                                    <Typography noWrap fontSize={11} color="text.secondary">{col.notes || '—'}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11 }}>{col.collected_by_name || '—'}</TableCell>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => openEditCol(plan.id, col)} sx={{ color: '#1565c0' }}>
                                      <Edit sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}
                </Card>
              );
            })
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={onClose} sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add/Edit Plan Dialog ── */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {editPlan ? 'Edit Plan' : 'New Payment Plan'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequency</InputLabel>
                <Select value={planForm.frequency} label="Frequency" onChange={e => setPlanForm(p => ({ ...p, frequency: e.target.value }))}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Amount (₹) *" type="number" value={planForm.amount}
                onChange={e => setPlanForm(p => ({ ...p, amount: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Advance Paid (₹)" type="number" value={planForm.advance_paid}
                onChange={e => setPlanForm(p => ({ ...p, advance_paid: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Start Date" type="date" value={planForm.start_date}
                onChange={e => setPlanForm(p => ({ ...p, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={planForm.notes}
                onChange={e => setPlanForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setPlanDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlan} disabled={savingPlan}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {savingPlan ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add/Edit Collection Dialog ── */}
      <Dialog open={colDialog} onClose={() => setColDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {editCol ? 'Edit Payment' : 'Record Payment'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Amount (₹) *" type="number" value={colForm.amount}
                onChange={e => setColForm(p => ({ ...p, amount: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Paid Date" type="date" value={colForm.paid_date}
                onChange={e => setColForm(p => ({ ...p, paid_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Due Date" type="date" value={colForm.due_date}
                onChange={e => setColForm(p => ({ ...p, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select value={colForm.method} label="Method" onChange={e => setColForm(p => ({ ...p, method: e.target.value }))}>
                  {Object.entries(METHOD_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={colForm.status} label="Status" onChange={e => setColForm(p => ({ ...p, status: e.target.value }))}>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={colForm.notes}
                onChange={e => setColForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>

            {/* Receipt Image Upload */}
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, color: '#475569' }}>
                Receipt / Payment Proof
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <label htmlFor="receipt-upload" style={{ flex: 1 }}>
                  <input
                    id="receipt-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleReceiptChange}
                  />
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<UploadFile />}
                    fullWidth
                    sx={{ borderStyle: 'dashed', borderColor: '#94a3b8', color: '#475569', justifyContent: 'flex-start' }}
                  >
                    {receiptFile ? receiptFile.name : 'Upload receipt image'}
                  </Button>
                </label>
                {receiptPreview && (
                  <Box
                    component="img"
                    src={receiptPreview}
                    alt="preview"
                    onClick={() => setViewImage(receiptPreview)}
                    sx={{
                      width: 56, height: 56, objectFit: 'cover',
                      borderRadius: 1, border: '2px solid #e2e8f0',
                      cursor: 'zoom-in', flexShrink: 0,
                      '&:hover': { borderColor: '#1a237e' },
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setColDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCol} disabled={savingCol}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {savingCol ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen image viewer ── */}
      <Dialog open={!!viewImage} onClose={() => setViewImage(null)} maxWidth="md" PaperProps={{ sx: { borderRadius: 2, bgcolor: '#000', p: 0 } }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setViewImage(null)}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1 }}
          >
            <ExpandLess sx={{ transform: 'rotate(45deg)' }} />
          </IconButton>
          {viewImage && (
            <Box
              component="img"
              src={viewImage}
              alt="Receipt"
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
            />
          )}
        </Box>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}

// ── Main Employees Page ───────────────────────────────────────────────────────
export default function Employees() {
  const [employees, setEmployees]   = useState([]);
  const [filtered, setFiltered]     = useState([]);
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(10);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEmp, setEditEmp]       = useState(null);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);
  const [snack, setSnack]           = useState({ open: false, msg: '', sev: 'success' });
  const [filterRole, setFilterRole] = useState('all');

  // Dynamic roles from DB
  const [availableRoles, setAvailableRoles] = useState(FALLBACK_ROLES);

  // Vendor payments dialog
  const [payDialogVendor, setPayDialogVendor] = useState(null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    const [empRes, rolesRes] = await Promise.all([
      InvokeApi(Config.base_url + apiList.getEmployees, { page: 1, per_page: 500 }),
      InvokeApi(Config.base_url + apiList.getRoles, {}),
    ]);
    if (empRes?.code === '200') {
      setEmployees((empRes.employees ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 })));
    }
    if (rolesRes?.code === '200' && (rolesRes.roles || []).length > 0) {
      setAvailableRoles(rolesRes.roles.filter(r => r.is_active));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(employees.filter(emp => {
      const matchSearch = !q || emp.name?.toLowerCase().includes(q) || emp.phone?.includes(q) || emp.email?.toLowerCase().includes(q);
      const roleList = Array.isArray(emp.roles) ? emp.roles : (emp.roles ? String(emp.roles).split(',').map(r => r.trim()) : []);
      const matchRole = filterRole === 'all' || roleList.includes(filterRole);
      return matchSearch && matchRole;
    }));
    setPage(0);
  }, [search, employees, filterRole]);

  const openAdd  = () => { setEditEmp(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditEmp(row);
    const roles = Array.isArray(row.roles) ? row.roles : (row.roles ? row.roles.split(',').map(r => r.trim()) : []);
    setForm({ name: row.name || '', phone: row.phone || '', email: row.email || '', password: '', roles });
    setDialogOpen(true);
  };

  const toggleRole = (role) => {
    setForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  const handleSave = async () => {
    const cleanPhone = form.phone.trim().replace(/[\s\-\(\)]/g, '');
    if (!form.name || !cleanPhone || !form.email || (!editEmp && !form.password)) {
      setSnack({ open: true, msg: 'Please fill all required fields.', sev: 'warning' }); return;
    }
    if (!/^\d{10}$/.test(cleanPhone)) {
      setSnack({ open: true, msg: 'Phone must be exactly 10 digits.', sev: 'warning' }); return;
    }
    if (form.roles.length === 0) {
      setSnack({ open: true, msg: 'Please select at least one role.', sev: 'warning' }); return;
    }
    setSaving(true);
    const endpoint = editEmp ? apiList.editEmployee : apiList.addEmployee;
    const payload  = editEmp
      ? { id: editEmp.id, name: form.name, phone: cleanPhone, email: form.email, roles: form.roles,
          ...(form.password.trim() ? { password: form.password.trim() } : {}) }
      : { name: form.name, phone: cleanPhone, email: form.email, password: form.password, roles: form.roles };
    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSaving(false);
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: editEmp ? 'Employee updated.' : 'Employee added.', sev: 'success' });
      setDialogOpen(false); fetchEmployees();
    } else { setSnack({ open: true, msg: res?.message || 'Save failed.', sev: 'error' }); }
  };

  const handleToggle = async (row) => {
    const res = await InvokeApi(Config.base_url + apiList.toggleEmployee, { id: row.id, active: !row.is_active });
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'Status updated.', sev: 'success' }); fetchEmployees();
    } else { setSnack({ open: true, msg: res?.message || 'Toggle failed.', sev: 'error' }); }
  };

  const isVendorEmployee = (emp) => {
    const roles = Array.isArray(emp.roles) ? emp.roles : (emp.roles ? String(emp.roles).split(',').map(r => r.trim()) : []);
    return roles.includes('vendor');
  };

  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Box>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      {/*<Box sx={{
        mb: 3, p: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 60%, #0369a1 100%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
            <Group sx={{ fontSize: 26, color: '#fff' }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#fff">Employee Manager</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.2 }}>
              {filtered.length} of {employees.length} employee{employees.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={openAdd}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700,
            border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 2,
            backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
          }}>
          Add Employee
        </Button>
      </Box>*/}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Employee Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} of {employees.length} employee{employees.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchEmployees()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            Add Employee
          </Button>
        </Box>
      </Box>

      {/* ── Stats row ─────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total',    val: employees.length, color: '#1a237e', icon: Shield  },
          { label: 'Active',   val: employees.filter(e => e.is_active).length,  color: '#15803d', icon: Lock },
          { label: 'Inactive', val: employees.filter(e => !e.is_active).length, color: '#dc2626', icon: LockOpen },
          { label: 'Roles',    val: availableRoles.length, color: '#7c3aed', icon: Settings },
        ].map(({ label, val, color, icon: Icon }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card sx={{ borderRadius: 2.5, border: `1px solid ${alpha(color, 0.2)}`, bgcolor: alpha(color, 0.05), boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: alpha(color, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 18, color }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} fontSize={24} sx={{ color }}>{val}</Typography>
                    <Typography fontSize={12} color="text.secondary" fontWeight={500}>{label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name, phone or email…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 240 }} />

          {/* Role filter chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label="All Roles"
              size="small"
              clickable
              onClick={() => setFilterRole('all')}
              sx={{
                fontWeight: 700, fontSize: 11,
                bgcolor: filterRole === 'all' ? '#1a237e' : '#f1f5f9',
                color: filterRole === 'all' ? '#fff' : '#64748b',
              }}
            />
            {availableRoles.map(r => (
              <Chip
                key={r.key}
                label={r.label}
                size="small"
                clickable
                onClick={() => setFilterRole(r.key)}
                sx={{
                  fontWeight: 700, fontSize: 11,
                  bgcolor: filterRole === r.key ? (r.color || '#1a237e') : alpha(r.color || '#1a237e', 0.08),
                  color: filterRole === r.key ? '#fff' : (r.color || '#1a237e'),
                  border: `1px solid ${alpha(r.color || '#1a237e', 0.3)}`,
                }}
              />
            ))}
          </Box>

          <Tooltip title="Refresh">
            <IconButton onClick={fetchEmployees} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 1.5 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['Name', 'Phone', 'Email', 'Roles', 'Active', 'Last Login', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No employees found</TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((emp) => (
                      <TableRow key={emp.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 34, height: 34, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                              {(emp.name || 'E')[0].toUpperCase()}
                            </Avatar>
                            <Typography fontWeight={600} fontSize={13}>{emp.name || 'Unknown'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{emp.phone || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{emp.email || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(() => {
                              const roleList = Array.isArray(emp.roles) ? emp.roles : (emp.roles ? String(emp.roles).split(',').map(r => r.trim()) : []);
                              return roleList.map(r => {
                                const rd = availableRoles.find(ar => ar.key === r);
                                const color = rd?.color || '#64748b';
                                return (
                                  <Chip key={r} label={rd?.label || r} size="small"
                                    sx={{
                                      fontWeight: 700, fontSize: 10, height: 22,
                                      bgcolor: alpha(color, 0.12),
                                      color,
                                      border: `1px solid ${alpha(color, 0.3)}`,
                                    }} />
                                );
                              });
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Switch size="small" checked={!!emp.is_active} onChange={() => handleToggle(emp)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' } }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {emp.last_login ? formatDateTime(emp.last_login) : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(emp)} sx={{ color: '#1565c0' }}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isVendorEmployee(emp) && (
                              <Tooltip title="Vendor Payments">
                                <IconButton size="small" onClick={() => setPayDialogVendor(emp)}
                                  sx={{ color: '#ff6f00', border: '1px solid #ffe0b2', borderRadius: 1 }}>
                                  <Payment fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filtered.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={pageSize}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
          </>
        )}
      </Card>

      {/* ── Add/Edit Employee Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {editEmp ? 'Edit Employee' : 'Add Employee'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Full Name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone * (10 digits)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                inputProps={{ maxLength: 10 }} helperText="Enter exactly 10 digits" />
            </Grid>
            <Grid item xs={12} sm={editEmp ? 6 : 6}>
              <TextField fullWidth size="small" label="Email *" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            {/* Password — required for add, optional for edit */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small"
                label={editEmp ? 'New Password (optional)' : 'Password *'}
                type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                helperText={editEmp ? 'Leave blank to keep current password' : ''} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1a237e' }}>Roles *</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {availableRoles.map(role => {
                  const checked = form.roles.includes(role.key);
                  return (
                    <Box
                      key={role.key}
                      onClick={() => toggleRole(role.key)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        px: 1.5, py: 0.8, borderRadius: 99, cursor: 'pointer',
                        bgcolor: checked ? alpha(role.color || '#1a237e', 0.12) : '#f8fafc',
                        border: `1.5px solid ${checked ? alpha(role.color || '#1a237e', 0.5) : '#e2e8f0'}`,
                        transition: 'all 0.15s',
                        userSelect: 'none',
                        '&:hover': { bgcolor: checked ? alpha(role.color || '#1a237e', 0.18) : '#f1f5f9' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        readOnly
                        sx={{
                          p: 0,
                          color: role.color || '#1a237e',
                          '&.Mui-checked': { color: role.color || '#1a237e' },
                        }}
                      />
                      <Typography fontSize={12} fontWeight={checked ? 700 : 500}
                        sx={{ color: checked ? (role.color || '#1a237e') : '#64748b' }}>
                        {role.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Vendor Payments Dialog ── */}
      <VendorPaymentsDialog
        vendor={payDialogVendor}
        open={!!payDialogVendor}
        onClose={() => setPayDialogVendor(null)}
      />

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
